import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Loader2, Save, X, Plus, Utensils } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MenuScannerPage() {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const [scanned, setScanned] = useState(false);

    const onDrop = useCallback(acceptedFiles => {
        const selected = acceptedFiles[0];
        if (selected) {
            setFile(selected);
            setPreview(URL.createObjectURL(selected));
            setScanned(false);
            setMenuItems([]);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        maxFiles: 1
    });

    const handleScan = async () => {
        if (!file) return;

        setScanning(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(`${API}/menu/scan`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true
            });
            setMenuItems(res.data);
            setScanned(true);
            toast.success('Menu zostało pomyślnie zeskanowane!');
        } catch (error) {
            console.error(error);
            toast.error('Błąd skanowania. Sprawdź czy klucz API jest poprawny.');
        } finally {
            setScanning(false);
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...menuItems];
        newItems[index] = { ...newItems[index], [field]: value };
        setMenuItems(newItems);
    };

    const handleDeleteItem = (index) => {
        const newItems = menuItems.filter((_, i) => i !== index);
        setMenuItems(newItems);
    };

    const handleAddItem = () => {
        setMenuItems([...menuItems, { name: '', price: '', description: '', category: 'Ogólne' }]);
    };

    const handleSave = () => {
        // Zapis do bazy nie jest jeszcze zaimplementowany (brak business_id w tym
        // widoku + brak typu bloku "menu" w builderze — realny zapis wymaga wyboru
        // firmy i nowego bloku, patrz SECURITY_FINDINGS.md). Zamiast udawac sukces,
        // mowimy to wprost.
        toast.error('Zapis menu do bazy jest w przygotowaniu — na razie wyniki skanowania mozna tylko przejrzec i skopiowac recznie.');
    };

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Utensils className="w-8 h-8 text-amber-500" />
                    Skaner Menu AI
                </h1>
                <p className="text-slate-500">Zrób zdjęcie karty menu, a AI zamieni je w cyfrowy cennik.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column: Upload & Preview */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Wgraj zdjęcie menu</CardTitle>
                            <CardDescription>Obsługiwane formaty: JPG, PNG, WEBP</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {!preview ? (
                                <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${isDragActive ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-500'}`}>
                                    <input {...getInputProps()} />
                                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                                    <p className="text-slate-600 font-medium">Przeciągnij zdjęcie tutaj lub kliknij</p>
                                    <p className="text-xs text-slate-400 mt-2">Maksymalny rozmiar: 5MB</p>
                                </div>
                            ) : (
                                <div className="relative rounded-xl overflow-hidden border border-slate-200">
                                    <img src={preview} alt="Menu preview" className="w-full h-auto object-cover" />
                                    <Button
                                        variant="destructive"
                                        size="icon"
                                        className="absolute top-2 right-2 rounded-full"
                                        onClick={() => { setFile(null); setPreview(null); setScanned(false); }}
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}

                            {file && !scanned && (
                                <Button
                                    onClick={handleScan}
                                    disabled={scanning}
                                    className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-white"
                                >
                                    {scanning ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Analizowanie przez AI...
                                        </>
                                    ) : (
                                        <>
                                            <FileText className="w-4 h-4 mr-2" />
                                            Skanuj Menu
                                        </>
                                    )}
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Right Column: Editor */}
                <div className="space-y-6">
                    {scanned ? (
                        <Card className="border-amber-200 shadow-md">
                            <CardHeader className="bg-amber-50/50 border-b border-amber-100">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-amber-900">Wynik Skanowania</CardTitle>
                                    <Button size="sm" variant="outline" onClick={handleSave} title="Zapis do bazy jest w przygotowaniu">
                                        <Save className="w-4 h-4 mr-2" /> Zapisz Menu (wkrótce)
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="max-h-[600px] overflow-y-auto p-6 space-y-4">
                                    {menuItems.map((item, index) => (
                                        <div key={index} className="flex gap-4 p-4 border rounded-lg bg-white group hover:border-amber-300 transition-colors">
                                            <div className="flex-1 space-y-3">
                                                <div className="flex gap-3">
                                                    <div className="flex-1">
                                                        <Label className="text-xs text-slate-400">Nazwa dania</Label>
                                                        <Input
                                                            value={item.name}
                                                            onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                                            className="font-medium"
                                                        />
                                                    </div>
                                                    <div className="w-24">
                                                        <Label className="text-xs text-slate-400">Cena</Label>
                                                        <Input
                                                            value={item.price}
                                                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                                            className="text-right"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-slate-400">Opis / Składniki</Label>
                                                    <Textarea
                                                        value={item.description || ''}
                                                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                                        className="h-16 text-sm resize-none"
                                                    />
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div className="w-1/2">
                                                        <Label className="text-xs text-slate-400">Kategoria</Label>
                                                        <Input
                                                            value={item.category}
                                                            onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                                                            className="h-8 text-xs bg-slate-50"
                                                        />
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleDeleteItem(index)}
                                                    >
                                                        Usuń
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <Button variant="outline" className="w-full border-dashed" onClick={handleAddItem}>
                                        <Plus className="w-4 h-4 mr-2" /> Dodaj Pozycję
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50 text-center">
                            <Utensils className="w-16 h-16 text-slate-300 mb-4" />
                            <h3 className="text-lg font-medium text-slate-900">Tutaj pojawią się wyniki</h3>
                            <p className="text-slate-500 max-w-xs mt-2">Wgraj zdjęcie menu po lewej stronie, aby rozpocząć edycję.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
